<template>
  <SectionFrame v-if="(shown && list.length) || inStudio" :section="section" :index="index" :heading="layout === 'badge' ? '' : heading" label="Reviews"
                :narrow="layout === 'quote' || layout === 'badge'" :center="layout === 'quote'">
    <template v-if="shown && list.length">
      <p v-if="summary.count && layout !== 'badge'" class="s2-rating-line mo-reveal">
        <span class="s2-stars" :aria-label="`${summary.average} out of 5`">{{ stars(summary.average) }}</span>
        <b>{{ summary.average }}</b><span class="s2-muted">from {{ summary.count }} {{ summary.count === 1 ? "review" : "reviews" }}<sup v-if="note">{{ note }}</sup></span>
      </p>
      <!-- Badge: a small rating strip for any page. -->
      <div v-if="layout === 'badge'" class="s2-badge t-card mo-reveal">
        <span class="s2-stars" aria-hidden="true">{{ stars(summary.average) }}</span>
        <span><b>{{ summary.average }} out of 5</b> <span class="s2-muted">· {{ summary.count }} {{ summary.count === 1 ? "review" : "reviews" }}</span></span>
        <q v-if="list[0]" class="s2-badge-q">{{ trim(list[0].body, 110) }}</q>
      </div>
      <figure v-else-if="layout === 'quote'" class="s2-bigquote mo-reveal">
        <span v-if="section.showStars !== false" class="s2-stars" :aria-label="`${list[0].rating} out of 5`">{{ stars(list[0].rating) }}</span>
        <blockquote class="t-display">“{{ list[0].body }}”</blockquote>
        <figcaption v-if="section.showName !== false" class="s2-muted">{{ who(list[0]) }}</figcaption>
      </figure>
      <div v-else-if="layout === 'carousel'" class="s2-rail" tabindex="0" role="region" :aria-label="`${heading || 'Reviews'}, scroll for more`">
        <figure v-for="r in list" :key="r.id" class="t-card s2-review">
          <component :is="'ReviewBody'" />
          <span v-if="section.showStars !== false" class="s2-stars" :aria-label="`${r.rating} out of 5`">{{ stars(r.rating) }}</span>
          <blockquote>“{{ r.body }}”</blockquote>
          <figcaption class="s2-review-who">
            <img v-if="section.showPhoto && r.photo" :src="r.photo" alt="" class="s2-avatar" loading="lazy">
            <span v-if="section.showName !== false">{{ who(r) }}</span>
            <span v-if="section.showDate && r.review_date" class="s2-muted s2-small">{{ niceDate(r.review_date) }}</span>
          </figcaption>
        </figure>
      </div>
      <div v-else class="s2-wall mo-stagger">
        <figure v-for="r in list" :key="r.id" class="t-card s2-review">
          <span v-if="section.showStars !== false" class="s2-stars" :aria-label="`${r.rating} out of 5`">{{ stars(r.rating) }}</span>
          <blockquote>“{{ r.body }}”</blockquote>
          <figcaption class="s2-review-who">
            <img v-if="section.showPhoto && r.photo" :src="r.photo" alt="" class="s2-avatar" loading="lazy">
            <span v-if="section.showName !== false">{{ who(r) }}</span>
            <span v-if="section.showDate && r.review_date" class="s2-muted s2-small">{{ niceDate(r.review_date) }}</span>
          </figcaption>
        </figure>
      </div>
    </template>
    <StudioGap v-else-if="!all.length">Add a review or two (Reviews in the studio), then they show here.</StudioGap>
    <StudioGap v-else>Shown on your site once you have {{ section.minReviews }} reviews ({{ all.length }} so far).</StudioGap>
  </SectionFrame>
</template>

<script setup>
import {computed} from "vue";
import SectionFrame from "./SectionFrame.vue";
import StudioGap from "./StudioGap.vue";
import {layoutOf, niceDate, pickReviews, reviewsShown, stars} from "@/utils/sectionLogic.js";
import {studioMode} from "@/classes/previewBridge.js";
import {useFootnotes} from "@/stores/footnotes.js";

const props = defineProps({section: {type: Object, required: true}, index: {type: Number, default: 0}, data: {type: Object, default: () => ({})}});
const inStudio = computed(() => studioMode.value !== "off");
const layout = computed(() => layoutOf(props.section, ["wall", "quote", "carousel", "badge"]));
const heading = computed(() => props.section.heading ?? "What customers say");
const all = computed(() => props.data.reviews || []);
const summary = computed(() => props.data.reviewSummary || {count: 0, average: null});
const shown = computed(() => reviewsShown(all.value, props.section));
const list = computed(() => pickReviews(all.value, {...props.section, count: layout.value === "quote" ? 1 : props.section.count}));
const who = (r) => [r.name, r.town].filter(Boolean).join(", ");
const trim = (s, n) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);
const note = summary.value.count && reviewsShown(all.value, props.section)
  ? useFootnotes().add(`Based on ${summary.value.count} ${summary.value.count === 1 ? "review" : "reviews"} customers left with us.`) : null;
</script>

<style>
.s2-stars { color: var(--t-accent); letter-spacing: .1em; }
.s2-rating-line { display: flex; flex-wrap: wrap; align-items: baseline; gap: .5rem; margin: -1rem 0 1.5rem; }
.s2-wall { display: grid; gap: var(--t-gap); grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr)); align-items: start; }
.s2-review { margin: 0; display: flex; flex-direction: column; gap: .6rem; }
.s2-review blockquote { margin: 0; line-height: var(--t-leading); }
.s2-review-who { display: flex; align-items: center; gap: .6rem; flex-wrap: wrap; font-weight: 600; }
.s2-avatar { width: 36px; height: 36px; border-radius: 50%; object-fit: cover; }
.s2-bigquote { margin: 0 auto; max-width: 46ch; }
.s2-bigquote blockquote { margin: .75rem 0; font-size: clamp(1.4rem, 3vw, 2.1rem); line-height: 1.25; font-family: var(--t-font-display); }
.s2-rail { display: grid; grid-auto-flow: column; grid-auto-columns: minmax(260px, 32%); gap: var(--t-gap); overflow-x: auto; scroll-snap-type: x mandatory; padding-bottom: .75rem; }
.s2-rail > * { scroll-snap-align: start; }
@media (max-width: 640px) { .s2-rail { grid-auto-columns: 82%; } }
.s2-badge { display: flex; flex-wrap: wrap; align-items: center; gap: .5rem 1rem; justify-content: center; text-align: center; }
.s2-badge-q { flex-basis: 100%; color: var(--t-muted); font-style: italic; }
</style>
